// @flow
import * as React from 'react'
import type { GoogleMapsAPI, GoogleMap } from 'types/google_maps'
import { type ControlPosition, controlPositions } from './control_positions'

type Props = {
  gMaps: GoogleMapsAPI,
  centerLat: number,
  centerLng: number,
  zoom: number,
  // takes precedence over center and zoom
  fitBounds: boolean,
  shouldUpdateMapParams: (prevProps: Props, currProps: Props) => boolean,
  scrollwheel: boolean,
  fullscreenControl: boolean,
  showMapTypeControl: boolean,
  showZoomControl: boolean,
  showStreetViewControl: boolean,
  zoomControlPosition: ControlPosition,
  streetViewControlPosition: ControlPosition,
  customButton?: {
    ctaText: string,
    onCtaClick: (map: GoogleMap) => void,
  },
  children: React.Node,
}

export default class MapEmbedPresentation extends React.Component<Props> {
  mapEl: HTMLElement
  map: GoogleMap
  customButton: ?HTMLElement
  handleCustomButtonClick: ?EventListener
  justUpdatedMapParams: ?boolean

  static defaultProps = {
    showMapTypeControl: false,
    showZoomControl: true,
    showStreetViewControl: true,
    zoomControlPosition: 'RIGHT_TOP',
    streetViewControlPosition: 'RIGHT_TOP',
  }

  constructor(props: Props) {
    super(props)

    if (props.customButton) {
      const { ctaText, onCtaClick } = props.customButton

      const customButton = document.createElement('button')
      customButton.appendChild(document.createTextNode(ctaText))
      customButton.className = 'MapEmbed-button'

      this.customButton = customButton
      this.handleCustomButtonClick = () => onCtaClick(this.map)
    }
  }

  componentDidMount = () => {
    const zoomControlPosition = controlPositions[this.props.zoomControlPosition]
    const streetViewControlPosition = controlPositions[this.props.streetViewControlPosition]

    this.map = new this.props.gMaps.Map(this.mapEl, {
      scrollwheel: this.props.scrollwheel,
      fullscreenControl: this.props.fullscreenControl,
      mapTypeControl: this.props.showMapTypeControl,
      zoomControl: this.props.showZoomControl,
      zoomControlOptions: { position: zoomControlPosition },
      streetViewControl: this.props.showStreetViewControl,
      streetViewControlOptions: { position: streetViewControlPosition },
    })

    if (this.customButton) {
      this.customButton.addEventListener('click', this.handleCustomButtonClick)
      this.map.addListener('idle', () => {
        // idle event is emitted once after updateMapParams is called
        if (this.justUpdatedMapParams) {
          this.hideCustomButton()
          this.justUpdatedMapParams = false
        } else {
          this.showCustomButton()
        }
      })
    }

    // the google maps object has been initialized so force a rerender so that any map
    // annotations will be rendered
    this.forceUpdate()
  }

  componentDidUpdate = (prevProps: Props) => {
    if (
      (!prevProps.children && this.props.children) ||
      this.props.shouldUpdateMapParams(prevProps, this.props)
    ) {
      this.updateMapParams()
    }
  }

  componentWillUnmount() {
    if (this.customButton) {
      this.customButton.removeEventListener('click', this.handleCustomButtonClick)
      this.props.gMaps.event.clearInstanceListeners(this.map)
    }
  }

  render = () => {
    return (
      <div className="MapEmbed" ref={(el: any) => (this.mapEl = el)}>
        {this.renderChildrenIfMapInitialized()}
      </div>
    )
  }

  renderChildrenIfMapInitialized = () => {
    if (!this.map) return null

    return React.Children.map(this.props.children, (child: any) => {
      return child ? React.cloneElement(child, { gMaps: this.props.gMaps, map: this.map }) : child
    })
  }

  updateMapParams = () => {
    this.map.setCenter(this.center)
    this.map.setZoom(this.props.zoom)

    if (this.props.fitBounds) this.fitBounds()

    this.justUpdatedMapParams = true
  }

  fitBounds = () => {
    const bounds = new this.props.gMaps.LatLngBounds()

    // use duck-typing to see if the children are points of interest, if they are, extends the
    // bounds to account for them
    React.Children.forEach(this.props.children, child => {
      if (!child) return

      const { lat, lng } = (child: any).props

      if (typeof lat === 'number' && typeof lng === 'number') bounds.extend({ lat, lng })
    })

    // if theere's nothing to fit to, bail
    if (bounds.isEmpty()) return

    this.map.fitBounds(bounds)

    // ensure zoom level is not too zoomed in after bound fitting
    if (this.map.getZoom() > this.props.zoom) this.map.setZoom(this.props.zoom)
  }

  showCustomButton = () => {
    if (
      this.customButton &&
      !this.map.controls[this.props.gMaps.ControlPosition.BOTTOM_CENTER].getLength()
    ) {
      this.map.controls[this.props.gMaps.ControlPosition.BOTTOM_CENTER].push(this.customButton)
    }
  }

  hideCustomButton = () => {
    if (this.customButton) this.map.controls[this.props.gMaps.ControlPosition.BOTTOM_CENTER].clear()
  }

  get center(): { lat: number, lng: number } {
    return { lat: this.props.centerLat, lng: this.props.centerLng }
  }
}
